import * as React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity, Brain, Building2, CircleDollarSign, FileText, Globe, Handshake, Home, Inbox, LogOut, Megaphone, Menu, MousePointerClick, Radar, Sparkles, Target, Users, Wallet, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

type NavItem = {
    to: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    testid?: string;
    badge?: number;
};
type NavGroup = { label?: string; items: NavItem[] };

/**
 * Server-side surface for the Unassigned inbox count.
 * Backend returns { count: number } (and may include per-type breakdowns we ignore here).
 * We treat any non-numeric/missing payload as zero so the badge never flashes garbage.
 */
interface UnassignedSummary {
    count?: number;
}

async function fetchUnassignedCount(): Promise<number> {
    const { data } = await api.get<UnassignedSummary>("/admin/unassigned");
    return typeof data?.count === "number" && data.count > 0 ? data.count : 0;
}

/**
 * Grouped nav: core operational items first, then Advertisers/Affiliates/
 * Unassigned as a "commerce" cluster, then a "Settings" cluster — each
 * separated by a divider. Memory moved out from between Constructor and
 * Domains — it's a constructor-internal concern, not a top-level op.
 *
 * The Unassigned entry is rendered conditionally: it only appears when
 * GET /admin/unassigned reports a non-zero count (refreshed every 60s
 * by the layout component). Keeping the entry hidden when empty avoids
 * a permanent "you have zero work to do" pacifier in the sidebar.
 */
function buildNavGroups(unassignedCount: number): NavGroup[] {
    const commerceItems: NavItem[] = [
        { to: "/advertisers", label: "Advertisers", icon: Building2, testid: "nav-advertisers" },
        { to: "/affiliates", label: "Affiliates", icon: Handshake, testid: "nav-affiliates" },
        { to: "/conversions", label: "Conversions", icon: CircleDollarSign, testid: "nav-conversions" },
        { to: "/finance", label: "Finance", icon: Wallet, testid: "nav-finance" },
    ];
    if (unassignedCount > 0) {
        commerceItems.push({
            to: "/unassigned",
            label: "Unassigned",
            icon: Inbox,
            testid: "nav-unassigned",
            badge: unassignedCount,
        });
    }
    return [
        {
            items: [
                { to: "/", label: "Overview", icon: Home },
                { to: "/pages", label: "Pages", icon: FileText },
                { to: "/constructor", label: "Constructor", icon: Sparkles, testid: "nav-constructor" },
                { to: "/domains", label: "Domains", icon: Globe },
                { to: "/campaigns", label: "Campaigns", icon: Megaphone },
                { to: "/pixels", label: "Pixels", icon: Target },
                { to: "/leads", label: "Leads", icon: Inbox },
                { to: "/visits", label: "Visits", icon: MousePointerClick },
            ],
        },
        { items: commerceItems },
        {
            label: "Settings",
            items: [
                { to: "/ad-sources", label: "Traffic networks", icon: Radar, testid: "nav-ad-sources" },
                { to: "/memory", label: "Memory", icon: Brain },
                { to: "/sessions", label: "AI sessions", icon: Activity, testid: "nav-sessions" },
                { to: "/admins", label: "Admins", icon: Users },
            ],
        },
    ];
}

export function AppLayout(): React.ReactNode {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);

    // Close mobile drawer on route change
    React.useEffect(() => setMobileOpen(false), [location.pathname]);

    // Poll the unassigned inbox so the sidebar badge stays fresh without a manual refresh.
    // 60s cadence matches the brief; we keep retry low so a flaky network doesn't spam
    // logs and we leave the previous value visible during refetch (no UI flicker).
    const { data: unassignedCount = 0 } = useQuery({
        queryKey: ["unassigned-count"],
        queryFn: fetchUnassignedCount,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
        retry: 1,
    });

    const navGroups = React.useMemo(() => buildNavGroups(unassignedCount), [unassignedCount]);
    const allItems = React.useMemo(() => navGroups.flatMap((g) => g.items), [navGroups]);

    const handleLogout = async (): Promise<void> => {
        // Light confirm — destructive enough that an accidental click shouldn't
        // sign out a session with unsaved work in Constructor/manifest/etc.
        if (!window.confirm("Sign out? Any unsaved work in editors will be lost.")) return;
        await logout();
        navigate("/login", { replace: true });
    };

    // Active nav label for mobile top bar.
    const activeItem = allItems.find((i) =>
        i.to === "/" ? location.pathname === "/" : location.pathname.startsWith(i.to)
    );

    const navContent = (
        <>
            <div className="flex h-14 items-center justify-between gap-2 border-b px-4">
                <Link
                    to="/"
                    className="flex items-center gap-2 rounded-md transition-colors hover:opacity-80"
                    aria-label="Landing Manager — go to Overview"
                >
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <Globe className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-semibold tracking-tight">Landing Manager</span>
                </Link>
                <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="md:hidden rounded-md p-1 text-muted-foreground hover:bg-accent"
                    aria-label="Close menu"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            <nav className="flex-1 space-y-1 p-3" aria-label="Main">
                {navGroups.map((group, gi) => (
                    <React.Fragment key={gi}>
                        {gi > 0 && (
                            <div className="my-2">
                                <div className="border-t border-border/70" />
                                {group.label && (
                                    <div className="mt-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/80">
                                        {group.label}
                                    </div>
                                )}
                            </div>
                        )}
                        {group.items.map(({ to, label, icon: Icon, testid, badge }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/"}
                                data-testid={testid ?? `nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    cn(
                                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                                        isActive
                                            ? "bg-accent text-accent-foreground"
                                            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                                    )
                                }
                            >
                                <Icon className="h-4 w-4" />
                                <span className="flex-1 truncate">{label}</span>
                                {typeof badge === "number" && badge > 0 && (
                                    <span
                                        data-testid={`${testid ?? `nav-${label.toLowerCase().replace(/\s+/g, "-")}`}-badge`}
                                        className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
                                        aria-label={`${badge} unassigned`}
                                    >
                                        {badge > 99 ? "99+" : badge}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </React.Fragment>
                ))}
            </nav>
            <div className="border-t p-3 text-xs">
                <Link
                    to="/admins"
                    className="mb-2 block truncate text-muted-foreground hover:text-foreground hover:underline"
                    title="Manage admins"
                >
                    {admin?.email ?? "—"}
                </Link>
                <Button
                    size="sm"
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={handleLogout}
                    data-testid="logout-button"
                >
                    <LogOut className="h-4 w-4" />
                    Sign out
                </Button>
            </div>
        </>
    );

    return (
        <div className="flex min-h-screen">
            {/* Desktop sidebar */}
            <aside
                data-testid="app-sidebar"
                className="sticky top-0 hidden h-screen w-60 flex-col border-r bg-card/50 md:flex"
            >
                {navContent}
            </aside>

            {/* Mobile drawer (overlay) */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div
                        className="absolute inset-0 bg-background/60 backdrop-blur-sm"
                        onClick={() => setMobileOpen(false)}
                        aria-hidden="true"
                    />
                    <aside className="relative flex h-screen w-60 flex-col border-r bg-card">
                        {navContent}
                    </aside>
                </div>
            )}

            <main className="flex-1 min-w-0">
                {/* Mobile top bar */}
                <div className="flex h-12 items-center gap-3 border-b bg-card/50 px-4 md:hidden">
                    <button
                        type="button"
                        onClick={() => setMobileOpen(true)}
                        className="rounded-md p-1 text-muted-foreground hover:bg-accent"
                        aria-label="Open menu"
                        data-testid="mobile-nav-button"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    <Link to="/" className="text-sm font-semibold hover:opacity-80" aria-label="Landing Manager">
                        Landing Manager
                    </Link>
                    {activeItem && (
                        <span className="truncate text-sm text-muted-foreground">
                            <span aria-hidden="true">·</span> {activeItem.label}
                        </span>
                    )}
                </div>
                <Outlet />
            </main>
        </div>
    );
}
